import { Controller } from "@hotwired/stimulus"
import { get } from "../forum/api_client"
import { loadTranslations, t } from "../forum/i18n"
import { escapeHtml, timeAgo } from "../forum/renderer"

export default class extends Controller {
  static targets = ["dropdown", "input", "results"]

  connect() {
    loadTranslations()
    this.debounceTimer = null
    this.selectedIndex = -1
    this.boundClose = this.closeOnOutsideClick.bind(this)
  }

  disconnect() {
    document.removeEventListener('click', this.boundClose)
  }

  onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 3 && this.resultsTarget.children.length > 0) {
      this.openDropdown()
    }
  }

  onInput() {
    clearTimeout(this.debounceTimer)
    this.selectedIndex = -1
    this.debounceTimer = setTimeout(() => this.search(), 300)
  }

  onKeydown(e) {
    switch (e.key) {
      case 'Escape':
        this.closeDropdown()
        this.inputTarget.blur()
        break
      case 'Enter':
        e.preventDefault()
        if (this.selectedIndex >= 0) {
          const items = this.resultsTarget.querySelectorAll('.forum-quick-search__item a')
          if (items[this.selectedIndex]) {
            items[this.selectedIndex].click()
            return
          }
        }
        this.goToFullSearch()
        break
      case 'ArrowDown':
        e.preventDefault()
        this.navigate(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        this.navigate(-1)
        break
    }
  }

  navigate(dir) {
    const items = this.resultsTarget.querySelectorAll('.forum-quick-search__item')
    if (items.length === 0) return
    items.forEach(el => el.classList.remove('forum-quick-search__item--active'))
    this.selectedIndex += dir
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0
    items[this.selectedIndex].classList.add('forum-quick-search__item--active')
    items[this.selectedIndex].scrollIntoView({ block: 'nearest' })
  }

  openDropdown() {
    this.dropdownTarget.classList.add('forum-quick-search-bar__dropdown--open')
    document.addEventListener('click', this.boundClose)
  }

  closeDropdown() {
    this.dropdownTarget.classList.remove('forum-quick-search-bar__dropdown--open')
    document.removeEventListener('click', this.boundClose)
    this.selectedIndex = -1
  }

  closeOnOutsideClick(e) {
    if (!this.element.contains(e.target)) this.closeDropdown()
  }

  async search() {
    const query = this.inputTarget.value.trim()
    if (query.length < 3) {
      this.closeDropdown()
      this.resultsTarget.innerHTML = ''
      return
    }

    this.openDropdown()
    this.resultsTarget.innerHTML = `<div class="forum-quick-search__hint text-muted small p-3"><i class="fa-solid fa-spinner fa-spin me-1"></i>${t('search.searching')}</div>`

    try {
      const response = await get('/search', { q: query, limit: 8 })
      const data = response.data || response
      this.renderQuickResults(data, query)
    } catch (e) {
      this.resultsTarget.innerHTML = `<div class="forum-quick-search__hint text-danger small p-3">${escapeHtml(e.message)}</div>`
    }
  }

  renderQuickResults(data, query) {
    const topics = (data.topics || []).slice(0, 5)
    const posts = (data.posts || []).slice(0, 3)

    if (topics.length === 0 && posts.length === 0) {
      this.resultsTarget.innerHTML = `<div class="forum-quick-search__hint text-muted small p-3">${t('search.no_results', { query: escapeHtml(query) })}</div>`
      return
    }

    let html = ''

    topics.forEach(topic => {
      const badge = topic.category_name
        ? `<span class="badge forum-search-category-badge" style="background-color: ${escapeHtml(topic.category_color || '#6c757d')}; font-size: 0.65rem;">${escapeHtml(topic.category_name)}</span>`
        : ''
      const avatar = topic.author_avatar_url
        ? `<img src="${escapeHtml(topic.author_avatar_url)}" class="forum-quick-search__avatar">`
        : `<span class="forum-quick-search__avatar forum-quick-search__avatar--placeholder">${escapeHtml(topic.author_initials || '?')}</span>`

      html += `
        <div class="forum-quick-search__item">
          <a href="/forum/topics/${topic.id}" class="forum-quick-search__link">
            <div class="forum-quick-search__icon"><i class="fa-solid fa-comments text-primary"></i></div>
            <div class="forum-quick-search__content">
              <div class="forum-quick-search__title">${escapeHtml(topic.title)} ${badge}</div>
              <div class="forum-quick-search__meta">${avatar} ${escapeHtml(topic.author_name)} &middot; ${timeAgo(topic.last_posted_at || topic.created_at)}</div>
            </div>
          </a>
        </div>`
    })

    posts.forEach(post => {
      const avatar = post.author_avatar_url
        ? `<img src="${escapeHtml(post.author_avatar_url)}" class="forum-quick-search__avatar">`
        : `<span class="forum-quick-search__avatar forum-quick-search__avatar--placeholder">${escapeHtml(post.author_initials || '?')}</span>`

      html += `
        <div class="forum-quick-search__item">
          <a href="/forum/topics/${post.topic_id}#post-${post.id}" class="forum-quick-search__link">
            <div class="forum-quick-search__icon"><i class="fa-solid fa-comment text-muted"></i></div>
            <div class="forum-quick-search__content">
              <div class="forum-quick-search__title">${escapeHtml(post.topic_title)} <span class="text-muted">#${post.post_number}</span></div>
              <div class="forum-quick-search__excerpt small text-muted">${post.excerpt || ''}</div>
              <div class="forum-quick-search__meta">${avatar} ${escapeHtml(post.author_name)} &middot; ${timeAgo(post.created_at)}</div>
            </div>
          </a>
        </div>`
    })

    const total = data.total_count || 0
    if (total > topics.length + posts.length) {
      html += `
        <div class="forum-quick-search__footer">
          <a href="/forum/search?q=${encodeURIComponent(this.inputTarget.value.trim())}" class="forum-quick-search__see-all">
            ${t('search.see_all')} (${total})
            <i class="fa-solid fa-arrow-right ms-1"></i>
          </a>
        </div>`
    }

    this.resultsTarget.innerHTML = html
  }

  goToFullSearch() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 3) {
      window.location.href = `/forum/search?q=${encodeURIComponent(query)}`
    }
  }
}
